<script setup lang="ts">
import { type VNode, h, toRefs } from 'vue';
import RichTextRenderer from './contentful-rich-text-vue-renderer.js';

import { LinkTargetType } from '@/models/LinkTargetType';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { isAbsoluteLink } from '@/helpers/link';

import { DKImage, DKLink, DKVideo } from '@dormakaba/dormakaba-components';

type DKRichTextJsonContentType = 'image/jpeg' | 'video/mp4' | 'application/pdf';

interface DKRichTextRenderNode {
  [BLOCKS.EMBEDDED_ASSET]: (node: any, key: string) => VNode | undefined;
  [BLOCKS.EMBEDDED_ENTRY]: (node: any, key: string) => VNode | undefined;
  [INLINES.HYPERLINK]: (node: any, key: string) => VNode | undefined;
}

const props = withDefaults(
  defineProps<{
    json: any;
    imageStack?: string;
  }>(),
  {
    imageStack: undefined,
  }
);

const { imageStack } = toRefs(props);

const nodeAssets = (node: any, key: string): VNode | undefined => {
  const file = node.data.target.fields?.file;
  if (!file) return;

  const content: DKRichTextJsonContentType = file.contentType;
  const fnList: {
    [key: string]: () => VNode;
  } = {
    image: (): VNode =>
      h(DKImage, {
        key,
        url: file.url,
        alt: file.title,
        stack: imageStack.value ?? 'getNewsDetailsBodyImageStack',
        frameType: 'full',
      }),
    video: (): VNode =>
      h(
        'video',
        {
          key,
          controls: true,
        },
        [h('source', { src: file.url, type: file.contentType })]
      ),
    pdf: (): VNode =>
      h(DKLink, {
        key,
        name: file.title,
        linkType: 'download',
        href: file.url,
        target: LinkTargetType.BLANK,
      }),
  };

  const asset = Object.keys(fnList).find((item) => content.includes(item));
  return asset ? fnList[asset]() : undefined;
};

const nodeEntries = (node: any, key: string): VNode | undefined => {
  const file = node.data.target.fields?.file;
  if (!file) return;

  return h(DKVideo, {
    key,
    video: file,
    showCaption: true,
    isAnimated: false,
    isFullRow: true,
  });
};

const nodeHyperlink = (node: any, key: string): VNode | undefined => {
  const { uri } = node.data;
  const target = isAbsoluteLink(uri)
    ? LinkTargetType.BLANK
    : LinkTargetType.SELF;

  return h(
    'a',
    { key, href: uri, target },
    { default: () => node.content[0].value }
  );
};

const renderNode = (): DKRichTextRenderNode => ({
  [BLOCKS.EMBEDDED_ASSET]: (node: any, key: string) => nodeAssets(node, key),
  [BLOCKS.EMBEDDED_ENTRY]: (node: any, key: string) => nodeEntries(node, key),
  [INLINES.HYPERLINK]: (node: any, key: string) => nodeHyperlink(node, key),
});
</script>

<template>
  <div>
    <rich-text-renderer :document="json" :node-renderers="renderNode()" />
  </div>
</template>
